import { ACCEPTED_FILE_TYPES, ContentType, EXTENSION_TO_CONTENT_TYPE } from '@constants';
import { CLAIM_MEDIA_TYPE } from '@pulse/constants/claims';
import retry from 'async-retry';

import ApiBase from './ApiBase';

const ACCEPTED_TYPES_SET: ReadonlySet<string> = new Set(ACCEPTED_FILE_TYPES);

export default class MediaService extends ApiBase {
  bucketName: string;

  constructor(apiBaseUrl: string, bucketName: string) {
    super(apiBaseUrl);

    this.bucketName = bucketName;
    this.baseUrl = `${this.baseUrl}/media/upload`;
  }

  private getContentTypeFromFilename(filename: string): string {
    if (!filename.includes('.')) {
      throw new Error(`Unsupported file type: unknown`);
    }

    const extension = filename.toLowerCase().split('.').pop();
    const mapped: ContentType | undefined = extension ? EXTENSION_TO_CONTENT_TYPE[extension] : undefined;

    return mapped ?? 'application/octet-stream';
  }

  private normalizeContentType(type: string, filename: string): string {
    // If the type is generic like "image", "video", etc., infer from filename
    if (type === 'image' || type === 'video' || !type) {
      return this.getContentTypeFromFilename(filename);
    }

    // If it's already a proper MIME type from the canonical list, use it
    if (ACCEPTED_TYPES_SET.has(type)) {
      return type;
    }

    // Fallback to filename-based detection
    return this.getContentTypeFromFilename(filename);
  }

  async getSignedUrl(file) {
    const filename = file?.name || file?.file?.name;
    const contentType = this.normalizeContentType(file.type, filename);

    const { data } = await this.client.post('', {
      contentType,
      filename,
      mediaType: CLAIM_MEDIA_TYPE,
    });

    return data;
  }

  async uploadFile(file, { fileName, signedUrl, contentType, publicUrl }) {
    const actualFile = file.file || file;

    const response = await fetch(signedUrl, {
      body: actualFile,
      headers: {
        'Content-Type': contentType,
      },
      method: 'PUT',
    });

    if (!response.ok) {
      throw new Error('Failed to upload file');
    }

    return publicUrl || `https://storage.cloud.google.com/${this.bucketName}/claim/${fileName}`;
  }

  async uploadFiles(files, errorMessage) {
    // TODO return array of files
    const uploaded: {
      caption: string;
      fileData: unknown;
    }[] = [];

    for (const file of files) {
      try {
        const result = await retry(
          async () => {
            const signed = await this.getSignedUrl(file);
            const result = await this.uploadFile(file, {
              ...signed,
              contentType: this.normalizeContentType(file.type, file.name || file.file?.name),
            });

            return result;
          },
          {
            maxTimeout: 3000,
            retries: 5,
          },
        );

        uploaded.push({
          caption: `Uploaded on ${new Date().toLocaleString()}`,
          fileData: result,
        });
      } catch (e) {
        console.log('Error uploading file', e);
        if (errorMessage) throw new Error(errorMessage);
      }
    }

    return uploaded.map(({ fileData }) => fileData);
  }
}
